import { IClassListItem } from "../../../model/respone/classes/IClassListItem";
import { hasScheduleConflict } from "../../../helpers/scheduleConflict";
import { getLopIndex, isMonChungPair, isMonChungIndexMismatch } from "../../../helpers/monChung";
import { buildNhomTuChonUsage, nhomTuChonBlock } from "../../../helpers/nhomTuChon";
import { IClassesReducer } from "../../reducer-model/classes/IClassesReducer";
import { eReducerStatusBase } from "../../reducer-model/eReducerStatusBase";

const initialState: IClassesReducer = {
    status: eReducerStatusBase.is_not_initialization,
    list: [],
    error_message: "",
};

const parseTrungLichCsv = (csv?: string): number[] => {
    if (!csv) return [];
    return csv
        .split(",")
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !isNaN(n) && n > 0);
};

const toCsv = (ids: number[]): string | undefined =>
    ids.length === 0 ? undefined : Array.from(new Set(ids)).join(",");

/**
 * Pass optimistic cap nhóm tự chọn (đợt chính khoá) — chạy SAU khi các nhánh trùng lịch / khoá nhóm
 * đã set xong. Tính usage theo nhóm từ tập lớp đã ĐK (reasonCode ALREADY_REGISTERED) rồi:
 *   • lớp đang eligible mà nhóm đã đủ → chặn (NHOM_TU_CHON_FULL) → FE ẩn nút.
 *   • lớp đang bị chặn bởi NHOM_TU_CHON_FULL mà nhóm đã hết đủ (do vừa huỷ) → mở lại.
 * Cap chỉ áp khi item mang so_mon_dang_ky/so_tin_chi_tu_chon > 0 (backend chỉ gửi khi đợt chính khoá).
 */
const applyNhomTuChonCap = (list: IClassListItem[]): IClassListItem[] => {
    const registeredIds = new Set<number>();
    for (const c of list) if (c.reasonCode === "ALREADY_REGISTERED") registeredIds.add(c.classId);
    const usage = buildNhomTuChonUsage(list, registeredIds);
    return list.map(c => {
        if (c.canRegister) {
            const block = nhomTuChonBlock(c, usage);
            return block
                ? { ...c, canRegister: false, reasonCode: "NHOM_TU_CHON_FULL", message: block }
                : c;
        }
        if (c.reasonCode === "NHOM_TU_CHON_FULL" && !nhomTuChonBlock(c, usage)) {
            return { ...c, canRegister: true, reasonCode: undefined, message: undefined };
        }
        return c;
    });
};

/**
 * Listen cross-domain registration actions để optimistic-update slotsLeft + trùng lịch
 * → tránh full GET /api/registration/classes mỗi khi register/cancel (50k SV peak = 350k extra calls).
 * Action type loose-typed (any) vì cross-domain — accept cả CLASSES_* và REG_*.
 */
export const classesReducer = (state: IClassesReducer = initialState, action: any): IClassesReducer => {
    switch (action.type) {
        case "CLASSES_LOAD_START":
            return { ...state, status: eReducerStatusBase.is_loading, error_message: "" };
        case "CLASSES_LOAD_SUCCESS":
            return { ...state, status: eReducerStatusBase.is_completed, list: action.payload };
        case "CLASSES_LOAD_ERROR":
            return { ...state, status: eReducerStatusBase.is_error, error_message: action.payload };

        // Đổi đợt → xoá list đợt cũ về initial (status is_not_initialization) để effect re-load đợt mới
        // mà không flash data cũ. LOAD_START dispatch ngay sau sẽ chuyển sang is_loading.
        case "CLASSES_RESET":
            return { ...initialState };

        // Bulk update slot từ /api/registration/slots (user chọn môn / click Tải lại).
        // Payload: Record<classId, slotsLeft>. Lớp không có trong payload → giữ nguyên slotsLeft cũ.
        case "CLASSES_UPDATE_SLOTS": {
            const slots: Record<number, number> = action.payload || {};
            return {
                ...state,
                list: state.list.map(c =>
                    slots[c.classId] !== undefined ? { ...c, slotsLeft: slots[c.classId] } : c
                ),
            };
        }

        // Optimistic local cập nhật khi SignalR confirm register cho lớp này:
        //  • lớp vừa register: -1 slot, flag canRegister=false (ALREADY_REGISTERED)
        //  • lớp khác trùng lịch: thêm classId vào iD_lops_TrungLich + flag SCHEDULE_CONFLICT
        // Tránh lỗi UX "đăng ký xong còn thấy lớp trùng vẫn enable, click mới báo trùng".
        case "REG_REGISTER_CONFIRMED": {
            const newRegId: number = action.payload.classId;
            const registeredClass = state.list.find(c => c.classId === newRegId);
            // Khoá nhóm lớp (rule phạm vi chuyên ngành nhóm lớp): đợt bật cờ + lớp vừa ĐK có nhóm (>0)
            // → các lớp khác nhóm bị disable ngay, không cần refetch list. Lớp chung (=0) không khoá.
            const lockNhomLop = !!registeredClass?.is_lock_nhom_lop && (registeredClass?.stu_nhomlop_ids ?? 0) > 0;
            const chosenNhomLopId = registeredClass?.stu_nhomlop_ids ?? 0;
            return {
                ...state,
                list: applyNhomTuChonCap(state.list.map((c: IClassListItem) => {
                    if (c.classId === newRegId) {
                        return {
                            ...c,
                            slotsLeft: Math.max(0, c.slotsLeft - 1),
                            canRegister: false,
                            reasonCode: "ALREADY_REGISTERED",
                            message: "Đã đăng ký lớp này.",
                        };
                    }
                    // Môn chung móc nối CÙNG nhóm lớp: học xen kẽ cùng giờ là hợp lệ → KHÔNG cờ trùng
                    // lịch giả (fix bug "ĐK Đọc-Viết 5 xong không ĐK được Nghe-Nói 5, phải F5"). Nhưng phải
                    // cùng "số lớp" — lệch index thì khoá. Đặt TRƯỚC hasScheduleConflict để partner không
                    // bị cờ trùng. Mirror backend EligibilityService / ClassListService.
                    if (registeredClass && isMonChungPair(c, registeredClass)) {
                        if (c.canRegister && isMonChungIndexMismatch(c, registeredClass)) {
                            return {
                                ...c,
                                canRegister: false,
                                reasonCode: "MON_CHUNG_INDEX_MISMATCH",
                                message: `Môn chung phải đăng ký cùng số lớp — bạn đã chọn lớp ${getLopIndex(registeredClass.ten_lop_hp)}.`,
                            };
                        }
                        return c; // index khớp (hoặc đã khoá vì lý do khác) → giữ nguyên, không báo trùng
                    }
                    if (registeredClass &&
                        hasScheduleConflict(c.thoiKhoaBieus, registeredClass.thoiKhoaBieus)) {
                        const trungIds = parseTrungLichCsv(c.iD_lops_TrungLich);
                        if (!trungIds.includes(newRegId)) trungIds.push(newRegId);
                        return {
                            ...c,
                            iD_lops_TrungLich: toCsv(trungIds),
                            canRegister: false,
                            // Giữ reasonCode/message gốc nếu đã có lý do khác (vd OUT_OF_PHAM_VI),
                            // chỉ set SCHEDULE_CONFLICT khi trước đó là eligible.
                            reasonCode: c.reasonCode ?? "SCHEDULE_CONFLICT",
                            message: c.message ?? "Trùng lịch với lớp đã đăng ký.",
                        };
                    }
                    // Khoá lớp khác nhóm (chỉ lớp đang eligible — giữ reasonCode gốc nếu đã có lý do khác).
                    if (lockNhomLop && c.canRegister
                        && (c.stu_nhomlop_ids ?? 0) > 0 && c.stu_nhomlop_ids !== chosenNhomLopId) {
                        return {
                            ...c,
                            canRegister: false,
                            reasonCode: c.reasonCode ?? "LOCKED_TO_OTHER_NHOM_LOP",
                            message: c.message ?? "Bạn đã chọn nhóm lớp khác trong đợt này — chỉ được đăng ký lớp cùng nhóm.",
                        };
                    }
                    return c;
                })),
            };
        }

        // Optimistic local khi cancel — slot trả lại Redis qua RollbackAsync.
        //  • lớp vừa cancel: +1 slot, restore canRegister nếu trước đó chỉ fail vì ALREADY_REGISTERED
        //  • lớp khác: remove cancelledId khỏi iD_lops_TrungLich; nếu hết conflict + reason là
        //    SCHEDULE_CONFLICT (do reducer set trước đó) → restore canRegister.
        case "REG_CANCEL_SUCCESS": {
            // Huỷ cặp LT-TH → nhiều lớp bị huỷ cùng lúc. Restore slot/canRegister cho TẤT CẢ.
            const cancelledIds = new Set<number>(action.payload.cancelledClassIds ?? [action.payload.classId]);
            // Khoá nhóm lớp: chỉ mở lại các lớp bị LOCKED khi SV KHÔNG còn lớp nào đã ĐK có nhóm (>0).
            // Lớp đã ĐK = reasonCode "ALREADY_REGISTERED" (loại các lớp đang huỷ).
            const stillHasNhomRegistered = state.list.some(c =>
                !cancelledIds.has(c.classId)
                && c.reasonCode === "ALREADY_REGISTERED"
                && (c.stu_nhomlop_ids ?? 0) > 0);
            return {
                ...state,
                list: applyNhomTuChonCap(state.list.map((c: IClassListItem) => {
                    // Strip cancelledId khỏi iD_lops_TrungLich cho MỌI lớp TRƯỚC khi áp restore.
                    // Nền vàng (hasConflict) phải phản ánh đúng tập lớp còn ĐK ở mọi nhánh — nhánh
                    // LOCKED/MON_CHUNG trước đây return sớm mà không clear iD_lops_TrungLich → bug
                    // "nút đăng ký đã sáng nhưng nền vàng trùng lịch không mất, phải F5".
                    const trungIds = parseTrungLichCsv(c.iD_lops_TrungLich);
                    const newTrung = trungIds.filter(id => !cancelledIds.has(id));
                    const stillConflicted = newTrung.length > 0;
                    const base = newTrung.length !== trungIds.length
                        ? { ...c, iD_lops_TrungLich: toCsv(newTrung) }
                        : c;

                    if (cancelledIds.has(c.classId)) {
                        const wasOnlyAlreadyRegistered = c.reasonCode === "ALREADY_REGISTERED";
                        return {
                            ...base,
                            slotsLeft: c.slotsLeft + 1,
                            canRegister: wasOnlyAlreadyRegistered ? true : c.canRegister,
                            reasonCode: wasOnlyAlreadyRegistered ? undefined : c.reasonCode,
                            message: wasOnlyAlreadyRegistered ? undefined : c.message,
                        };
                    }
                    // Mở lại lớp bị khoá nhóm khi không còn lớp ĐK có nhóm nào — và đã hết trùng lịch.
                    if (c.reasonCode === "LOCKED_TO_OTHER_NHOM_LOP" && !stillHasNhomRegistered && !stillConflicted) {
                        return { ...base, canRegister: true, reasonCode: undefined, message: undefined };
                    }
                    // Mở lại lớp bị khoá vì lệch "số lớp" môn chung khi không còn partner lệch — và hết trùng lịch.
                    if (c.reasonCode === "MON_CHUNG_INDEX_MISMATCH") {
                        const stillMismatch = state.list.some(r =>
                            !cancelledIds.has(r.classId)
                            && r.reasonCode === "ALREADY_REGISTERED"
                            && isMonChungIndexMismatch(c, r));
                        if (!stillMismatch && !stillConflicted) {
                            return { ...base, canRegister: true, reasonCode: undefined, message: undefined };
                        }
                        return base;
                    }
                    // Trùng lịch thuần: restore khi hết conflict + reason là SCHEDULE_CONFLICT.
                    // Không restorable → trả base (giữ nguyên ref `c` khi không strip gì → tránh tạo
                    // object thừa cho toàn bộ list mỗi lần huỷ).
                    const restorable = !stillConflicted && c.reasonCode === "SCHEDULE_CONFLICT";
                    if (restorable) {
                        return { ...base, canRegister: true, reasonCode: undefined, message: undefined };
                    }
                    return base;
                })),
            };
        }

        default:
            return state;
    }
};
